import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import {
  buildKiwiAffiliateUrl,
  TRAVELER_TOOLBOX_LINKS,
  TRAVELER_TOOLBOX_WIDGETS,
  validateAiraloAffiliateUrl,
} from "../../lib/travelerAffiliateLinks";

import type {
  TravelerToolboxCategory,
} from "../../lib/travelerAffiliateLinks";

type TravelerToolboxProps = {
  category: TravelerToolboxCategory;
};

const WIDGET_MESSAGE_TYPE = "forfeo:affiliate-widget";
const WIDGET_READY_TIMEOUT_MS = 8000;

const sharedLinkClasses =
  "inline-flex min-h-11 items-center justify-center rounded-xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2";

function Disclosure({
  children,
  affiliate,
}: {
  children: ReactNode;
  affiliate: boolean;
}) {
  return (
    <p
      className="mt-3 text-xs leading-5 text-slate-500"
      data-affiliate-disclosure={
        affiliate ? "affiliate" : "direct-non-affiliate"
      }
    >
      {children}
    </p>
  );
}

function PartnerSearchFrame({
  category,
}: {
  category: TravelerToolboxCategory;
}) {
  const widget = TRAVELER_TOOLBOX_WIDGETS[category];
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">(
    "loading",
  );
  const [height, setHeight] = useState(widget.minHeight);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const data = event.data as
        | {
            type?: string;
            widget?: string;
            ready?: boolean;
            height?: number;
          }
        | null;

      if (!data || data.type !== WIDGET_MESSAGE_TYPE) {
        return;
      }

      if (data.widget !== widget.id) {
        return;
      }

      if (typeof data.height === "number" && data.height > 0) {
        setHeight(Math.min(Math.max(data.height, widget.minHeight), 1500));
      }

      if (data.ready === true) {
        setState("ready");
      }
    },
    [widget.id, widget.minHeight],
  );

  useEffect(() => {
    setState("loading");
    setHeight(widget.minHeight);

    window.addEventListener("message", handleMessage);

    const timer = window.setTimeout(() => {
      setState((current) => (current === "ready" ? current : "failed"));
    }, WIDGET_READY_TIMEOUT_MS);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.clearTimeout(timer);
    };
  }, [handleMessage, widget.id, widget.minHeight]);

  if (state === "failed") {
    return (
      <p
        className="mt-4 rounded-xl bg-slate-100 px-4 py-3 text-xs leading-5 text-slate-600"
        data-forfeo-toolbox-frame-state="failed"
      >
        Le comparateur partenaire n&rsquo;a pas pu se charger. Utilisez le
        lien ci-dessous pour poursuivre votre recherche.
      </p>
    );
  }

  return (
    <div
      className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white"
      data-forfeo-toolbox-frame-state={state}
    >
      <iframe
        ref={frameRef}
        data-forfeo-toolbox-frame="true"
        data-forfeo-toolbox-widget={widget.id}
        title={widget.title}
        src={widget.src}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin"
        className="w-full border-0"
        style={{ height: height + "px", minHeight: widget.minHeight + "px" }}
      />
    </div>
  );
}

type InternalPartnerBrowserProps = {
  affiliate: boolean;
  category: TravelerToolboxCategory;
  href: string;
  partner: string;
};

function InternalPartnerBrowser({
  affiliate,
  category,
  href,
  partner,
}: {
  affiliate: boolean;
  category: TravelerToolboxCategory;
  href: string;
  partner: string;
}) {
  const linkRel = affiliate
    ? "sponsored noopener noreferrer"
    : "noopener noreferrer";

  const displayPartner =
    category === "autos"
      ? "QEEQ"
      : category === "experiences"
        ? "Klook"
        : partner;

  return (
    <div
      className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5"
      data-toolbox-handoff={category}
    >
      <p className="text-sm font-semibold leading-6 text-slate-800">
        Le site du partenaire ne permet pas l&rsquo;affichage complet dans Forfeo.
      </p>

      <p className="mt-1 text-xs leading-5 text-slate-600">
        Votre recherche Forfeo reste ouverte dans cet onglet pendant que vous
        poursuivez chez {displayPartner}.
      </p>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <a
          className={sharedLinkClasses + " w-full sm:w-auto"}
          data-toolbox-external-handoff={category}
          data-toolbox-link={category}
          href={href}
          rel={linkRel}
          target="_blank"
        >
          Continuer chez {displayPartner}
        </a>

        <span className="text-xs leading-5 text-slate-500">
          Ouverture sécurisée dans un nouvel onglet.
        </span>
      </div>
    </div>
  );
}


function FlightsTool() {
  const [from, setFrom] = useState("YUL");
  const [to, setTo] = useState("YQB");
  const [departure, setDeparture] = useState("");
  const [returnDate, setReturnDate] = useState("");

  const flightUrl = useMemo(() => {
    try {
      return buildKiwiAffiliateUrl({
        from,
        to,
        departure,
        returnDate,
      });
    } catch {
      return null;
    }
  }, [departure, from, returnDate, to]);

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
      data-affiliate-status="affiliate-verified"
    >
      <h3 className="text-xl font-bold text-slate-950">Comparer des vols</h3>

      <p className="mt-2 text-sm leading-6 text-slate-600">
        Recherchez et réservez directement ici, ou préparez votre recherche
        puis continuez sur Kiwi.
      </p>

      <PartnerSearchFrame category="monde" />

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <label className="grid gap-2 text-sm font-medium text-slate-700">
          Départ
          <input
            className="min-h-11 rounded-xl border border-slate-300 px-3 py-2 uppercase"
            maxLength={8}
            onChange={(event) => setFrom(event.target.value)}
            value={from}
          />
        </label>

        <label className="grid gap-2 text-sm font-medium text-slate-700">
          Destination
          <input
            className="min-h-11 rounded-xl border border-slate-300 px-3 py-2 uppercase"
            maxLength={8}
            onChange={(event) => setTo(event.target.value)}
            value={to}
          />
        </label>

        <label className="grid gap-2 text-sm font-medium text-slate-700">
          Aller
          <input
            className="min-h-11 rounded-xl border border-slate-300 px-3 py-2"
            onChange={(event) => setDeparture(event.target.value)}
            type="date"
            value={departure}
          />
        </label>

        <label className="grid gap-2 text-sm font-medium text-slate-700">
          Retour
          <input
            className="min-h-11 rounded-xl border border-slate-300 px-3 py-2"
            min={departure || undefined}
            onChange={(event) => setReturnDate(event.target.value)}
            type="date"
            value={returnDate}
          />
        </label>
      </div>

      {flightUrl ? (

        <InternalPartnerBrowser
        affiliate
        category="monde"
        href={flightUrl}
        partner="Kiwi"
      />
      ) : (
        <p
          className="mt-5 rounded-xl bg-amber-50 px-4 py-3 text-xs font-semibold leading-5 text-amber-800"
          data-toolbox-link-disabled="monde"
        >
          Indiquez un aéroport de départ et une destination pour ouvrir la
          recherche Kiwi.
        </p>
      )}

      <Disclosure affiliate>
        Lien affilié Travelpayouts. Une attribution correctement marquée ne
        garantit pas qu&rsquo;une commission sera acceptée ou versée.
      </Disclosure>
    </div>
  );
}

function PartnerTool({
  affiliate,
  category,
  description,
  href,
  label,
  partner,
}: {
  affiliate: boolean;
  category: "autos" | "experiences";
  description: string;
  href: string;
  label: string;
  partner: string;
}) {
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
      data-affiliate-status={
        affiliate ? "affiliate-verified" : "direct-non-affiliate"
      }
    >
      <h3 className="text-xl font-bold text-slate-950">{partner}</h3>

      <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>

      <PartnerSearchFrame category={category} />


      <InternalPartnerBrowser
        affiliate={false}
        category={category}
        href={href}
        partner={partner}
      />

      <Disclosure affiliate={affiliate}>
        {affiliate
          ? "Recherche partenaire affiliée. Aucune commission n’est garantie."
          : "Lien direct non affilié. Forfeo ne garantit aucune commission sur cette redirection."}
      </Disclosure>
    </div>
  );
}

function EsimTool() {
  const airaloUrl = TRAVELER_TOOLBOX_LINKS.airaloAffiliate;
  const validAffiliateUrl = validateAiraloAffiliateUrl(airaloUrl);

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
      data-affiliate-status="affiliate-verified"
    >
      <h3 className="text-xl font-bold text-slate-950">eSIM de voyage</h3>

      <p className="mt-2 text-sm leading-6 text-slate-600">
        Comparez et achetez votre forfait de données directement ici.
      </p>

      <PartnerSearchFrame category="esim" />

      {validAffiliateUrl ? (

        <InternalPartnerBrowser
        affiliate
        category="esim"
        href={airaloUrl}
        partner="Airalo"
      />
      ) : null}

      <Disclosure affiliate>
        Lien affilié marqué. Le domaine peut être réécrit vers emrldco.com
        lorsque les paramètres Travelpayouts et la destination Airalo sont
        conservés. Aucune commission n&rsquo;est garantie.
      </Disclosure>
    </div>
  );
}

export function TravelerToolbox({ category }: TravelerToolboxProps) {
  return (
    <section
      aria-label="Traveler Toolbox Forfeo"
      className="w-full rounded-3xl bg-slate-50 p-4 sm:p-6"
      data-category={category}
      data-testid="traveler-toolbox"
    >
      <div className="mb-5">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
          Explorer Plus
        </p>

        <h2 className="mt-2 text-2xl font-bold text-slate-950">
          Traveler Toolbox Forfeo
        </h2>

        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
          Recherchez et réservez directement dans Forfeo, avec repli sur le
          lien partenaire si le comparateur ne répond pas.
        </p>
      </div>

      {category === "monde" && <FlightsTool />}

      {category === "autos" && (
        <PartnerTool
          affiliate
          category="autos"
          description="Comparez les véhicules disponibles et réservez sans quitter Forfeo."
          href={TRAVELER_TOOLBOX_LINKS.qeeqDirect}
          label="Ouvrir QEEQ"
          partner="Location de voitures"
        />
      )}

      {category === "experiences" && (
        <PartnerTool
          affiliate
          category="experiences"
          description="Découvrez et réservez des activités et attractions à destination."
          href={TRAVELER_TOOLBOX_LINKS.klookDirect}
          label="Ouvrir Klook"
          partner="Activités"
        />
      )}

      {category === "esim" && <EsimTool />}
    </section>
  );
}
